import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Board from "react-trello";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CachedIcon from "@material-ui/icons/Cached";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import NewTicketModal from "../../components/NewTicketModal";
import SetCelularModal from "../../components/SetCelularModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getOpportunitiesViewRules } from "../../utils/adminAccess";

import OpportunityCard from "./OpportunityCard";
import OpportunityTable from "./OpportunityTable";
import CreateContactModal from "./CreateContactModal";
import {
  URGENCY_CONFIG,
  URGENCY_ORDER,
  isTerminalStage,
  stageBadgeColor,
  urgencyColor,
} from "./helpers";

/** Stages que contam para o resumo de urgência do topo (cards ainda abertos). */
const OPEN_STAGES = ["novo", "em_negociacao", "aguardando_cliente"];

const CARDS_PER_STAGE_STEP = 50;

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: {
    minWidth: 240,
  },
  summary: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
    flexWrap: "wrap",
  },
  summaryChip: {
    color: "#FFF",
    fontWeight: "bold",
  },
  boardWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    ...theme.scrollbarStyles,

    // O react-trello fixa `max-height: 90vh` no ScrollableLane, a área rolável
    // de cada coluna (node_modules/react-trello/dist/styles/Base.js). 90vh é
    // maior que o espaço real disponível, que já perdeu a AppBar, o padding do
    // MainContainer e a barra de filtros — o excedente cai fora da tela e os
    // últimos cards ficam inalcançáveis.
    //
    // Não dá para corrigir por prop: o ScrollableLane não recebe `style`
    // (Lane.js:218). Sobrescrevemos por CSS a partir daqui. A classe
    // `react-trello-lane` na <section> é estável (Lane.js:321) e o
    // ScrollableLane é o <div> filho direto que embrulha o
    // `.smooth-dnd-container.vertical` — o irmão anterior é um <header>.
    //
    // O limite é em `vh`, não em `%`, de propósito. A primeira versão desta
    // correção usou `max-height: 100%` e trocou um bug por outro pior: o
    // percentual só resolve se TODOS os ancestrais tiverem altura definida, e
    // a cadeia aqui tem `section.react-trello-lane { height: auto }` no meio.
    // Sem resolver, o navegador computa `max-height: none` — a coluna fica sem
    // limite nenhum, o conteúdo nunca excede a caixa e `overflow-y: auto` não
    // gera barra de rolagem. Deixou de rolar por completo. `vh` resolve
    // sempre, independente de ancestral.
    //
    // Descontos medidos no código (conservadores: melhor sobrar espaço do que
    // a coluna vazar para fora da tela):
    //   48  appBarSpacer (layout/index.js)
    //   32  padding vertical do MainContainer (spacing(2) × 2)
    //   46  MainHeader (Typography h5 + gutterBottom + padding-bottom 6)
    //   48  Tabs (minHeight padrão do MUI v4)
    //   48  barra de filtros em UMA linha (padding 16 + marginBottom 8 + 40 do
    //       campo de busca) — ver ressalva abaixo
    //   10  padding do BoardWrapper do react-trello (5 × 2)
    //   30  margin (5 × 2) + padding (10 × 2) da própria lane
    //   42  LaneHeader (título + margin-bottom 10)
    //   10  margin-top do ScrollableLane
    //
    // A barra de filtros tem flexWrap: em tela estreita os chips quebram linha
    // e ela passa de ~48px para até ~140px. O desconto aqui assume UMA linha,
    // calibrado contra a tela real em 19/09/2026 — a versão anterior reservava
    // as 3 linhas e sobrava uma faixa vazia grande embaixo da coluna. Se em
    // tela estreita a coluna passar da viewport, é este número que volta a subir.
    "& .react-trello-lane": {
      maxHeight: "calc(100vh - 220px)",
    },
    "& .react-trello-lane > div": {
      // Folga proposital de 60px sobre o limite da lane: quando a altura da
      // lane resolve normalmente, quem manda é o flex e este teto nem entra em
      // ação — ele é a rede de segurança para o caso de a cadeia falhar.
      maxHeight: "calc(100vh - 280px)",
      // Sem isto, um flex item não encolhe abaixo do tamanho do conteúdo e o
      // overflow-y nunca chega ao fim da lista.
      minHeight: 0,
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  hint: {
    padding: theme.spacing(1),
  },
}));

/**
 * Kanban de Oportunidades de renovação.
 *
 * A prioridade é do servidor, não desta tela: o backend calcula o bucket de
 * urgência a partir da data de fim de vigência e devolve cada coluna já
 * ordenada (vencida e urgente no topo, depois quem vence antes). Esta tela
 * renderiza os cards na ordem recebida e, depois de qualquer arraste,
 * recarrega o board — inclusive quando o arraste foi dentro da mesma coluna,
 * para desfazer a reordenação local do react-trello. Ordem de card nunca é
 * decisão manual.
 *
 * Mover um card entre colunas é a única escrita daqui (PATCH de stage).
 */
const Opportunities = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const rules = getOpportunitiesViewRules(user);

  const [tab, setTab] = useState("board");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [limitPerStage, setLimitPerStage] = useState(CARDS_PER_STAGE_STEP);

  const [searchParam, setSearchParam] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState([]);

  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketContact, setTicketContact] = useState({});

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalOpportunity, setContactModalOpportunity] = useState(null);

  // Caminho novo (sem PolicyImportStaging) do mesmo botão "Cadastrar
  // contato" — apólices migradas direto, sem conciliação. Ver
  // handleOpenCreateContact.
  const [celularModalOpen, setCelularModalOpen] = useState(false);
  const [celularModalOpportunity, setCelularModalOpportunity] = useState(null);

  // Evita que o scroll no fim da coluna dispare vários incrementos seguidos
  // enquanto o board anterior ainda não voltou.
  const loadingMoreRef = useRef(false);

  const fetchBoard = useCallback(async () => {
    try {
      const { data } = await api.get("/opportunities/board", {
        params: { limit: limitPerStage },
      });
      setBoard(data);
    } catch (err) {
      toastError(err);
    }
    loadingMoreRef.current = false;
    setLoading(false);
  }, [limitPerStage]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const handleOpenTicket = (contact) => {
    if (!contact?.id) return;
    setTicketContact(contact);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = (ticket) => {
    setTicketModalOpen(false);
    setTicketContact({});
    if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
  };

  /**
   * Mesmo botão do card, dois destinos: apólice com PolicyImportStaging
   * pendente usa o caminho antigo (conciliação em lote); apólice sem
   * importStaging (ex.: as migradas do Quiver) usa o caminho novo, direto
   * por CPF/CNPJ via PUT /contatos/celular. canCreateContact no
   * OpportunityCard já garante que só chega aqui quem pode seguir um dos
   * dois caminhos.
   */
  const handleOpenCreateContact = (opportunity) => {
    if (opportunity?.policy?.importStaging) {
      setContactModalOpportunity(opportunity);
      setContactModalOpen(true);
    } else {
      setCelularModalOpportunity(opportunity);
      setCelularModalOpen(true);
    }
  };

  const handleCloseCreateContact = () => {
    setContactModalOpen(false);
    setContactModalOpportunity(null);
  };

  const handleCloseCelularModal = () => {
    setCelularModalOpen(false);
    setCelularModalOpportunity(null);
  };

  /**
   * Atualiza o card em memória com o contato que acabou de ser criado — sem
   * recarregar o board. O próprio create-contact já recalcula a oportunidade
   * daquele contato de forma síncrona no backend antes de responder (ver
   * CreatePendingContactService.ts), então a resposta já traz o contato
   * definitivo: não há necessidade de uma segunda chamada para "confirmar"
   * nada, nem de um recálculo em lote da empresa inteira (que além de caro
   * exigiria perfil admin, e cadastrar contato é tarefa de qualquer usuário).
   */
  const handleContactCreated = (policyId, contact) => {
    setBoard((previous) => {
      if (!previous) return previous;
      const next = {};
      Object.keys(previous).forEach((stage) => {
        const column = previous[stage];
        next[stage] = {
          ...column,
          opportunities: column.opportunities.map((opportunity) =>
            opportunity.policy?.id === policyId ? { ...opportunity, contact } : opportunity
          ),
        };
      });
      return next;
    });
  };

  /**
   * Salva a anotação livre da apólice (Requisito B) e atualiza o card em
   * memória — sem recarregar o board, mesmo padrão de handleContactCreated.
   * Devolve true/false em vez de propagar a exceção: o card usa isso para
   * decidir se recolhe a caixa de texto ou mantém o rascunho aberto quando
   * o salvamento falha.
   */
  const handleSaveNotes = async (policyId, notes) => {
    try {
      await api.put(`/policies/${policyId}/notes`, { notes });
      setBoard((previous) => {
        if (!previous) return previous;
        const next = {};
        Object.keys(previous).forEach((stage) => {
          const column = previous[stage];
          next[stage] = {
            ...column,
            opportunities: column.opportunities.map((opportunity) =>
              opportunity.policy?.id === policyId
                ? { ...opportunity, policy: { ...opportunity.policy, notes } }
                : opportunity
            ),
          };
        });
        return next;
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  /**
   * Salva Seguradora/Valor do card (Requisito A) — sem restrição por stage,
   * vale em qualquer coluna do Kanban. Atualiza o card com o que o backend
   * devolveu (não com o rascunho local), porque insurer passa por
   * sanitização no servidor e o valor exibido deve refletir o que foi
   * realmente gravado. Mesmo padrão de retorno true/false de handleSaveNotes.
   */
  const handleSaveKanbanFields = async (policyId, { insurer, premium }) => {
    try {
      const { data } = await api.put(`/policies/${policyId}/kanban-fields`, { insurer, premium });
      setBoard((previous) => {
        if (!previous) return previous;
        const next = {};
        Object.keys(previous).forEach((stage) => {
          const column = previous[stage];
          next[stage] = {
            ...column,
            opportunities: column.opportunities.map((opportunity) =>
              opportunity.policy?.id === policyId
                ? { ...opportunity, policy: { ...opportunity.policy, insurer: data.insurer, premium: data.premium } }
                : opportunity
            ),
          };
        });
        return next;
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const { data } = await api.post("/opportunities/recalculate");
      toast.success(
        `Kanban recalculado: ${data.created} card(s) novo(s), ${data.updated} atualizado(s).`
      );
      await fetchBoard();
    } catch (err) {
      toastError(err);
    }
    setRecalculating(false);
  };

  /**
   * Persiste a mudança de coluna e recarrega. Devolve true para o react-trello
   * aplicar o movimento na hora; se o PATCH falhar, o reload traz o card de
   * volta para onde o servidor diz que ele está.
   */
  const handleDragEnd = (cardId, sourceLaneId, targetLaneId) => {
    if (sourceLaneId === targetLaneId) {
      // Arraste dentro da mesma coluna: nada a persistir, e a ordem local
      // que o react-trello acabou de aplicar não vale — recarrega.
      fetchBoard();
      return true;
    }

    (async () => {
      try {
        await api.patch(`/opportunities/${cardId}/stage`, { stage: targetLaneId });
        toast.success("Oportunidade movida.");
      } catch (err) {
        toastError(err);
      }
      await fetchBoard();
    })();

    return true;
  };

  const toggleUrgency = (bucket) => {
    setUrgencyFilter((previous) =>
      previous.includes(bucket)
        ? previous.filter((item) => item !== bucket)
        : [...previous, bucket]
    );
  };

  const matchesFilters = useCallback(
    (opportunity) => {
      if (urgencyFilter.length > 0 && !urgencyFilter.includes(opportunity.urgencyBucket)) {
        return false;
      }
      const term = searchParam.trim().toLowerCase();
      if (!term) return true;
      const haystack = [
        opportunity.contact?.name,
        opportunity.contact?.number,
        opportunity.policy?.product,
        opportunity.policy?.insurer,
        opportunity.policy?.clientNameRaw,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    },
    [searchParam, urgencyFilter]
  );

  const urgencySummary = useMemo(() => {
    if (!board) return {};
    const totals = {};
    OPEN_STAGES.forEach((stage) => {
      (board[stage]?.opportunities || []).forEach((opportunity) => {
        totals[opportunity.urgencyBucket] = (totals[opportunity.urgencyBucket] || 0) + 1;
      });
    });
    return totals;
  }, [board]);

  const anyLaneHasMore = useMemo(
    () => Boolean(board) && Object.values(board).some((column) => column.hasMore),
    [board]
  );

  /**
   * Coluna que bateu no teto de cards por requisição do backend: ainda faltam
   * cards e pedir mais não adianta. Precisa aparecer na tela — o modo de falha
   * antigo era silencioso: a lista só "acabava" antes do fim, sem erro nenhum.
   */
  const lanesAtLimit = useMemo(
    () =>
      board
        ? Object.values(board)
            .filter((column) => column.limitReached)
            .map((column) => `${column.stageLabel} (${column.count})`)
        : [],
    [board]
  );

  /**
   * Chamado pelo react-trello ao chegar ao fim de uma coluna. O contrato dele
   * é devolver uma Promise de cards, que ele anexaria ao próprio estado — por
   * isso resolvemos com lista vazia de propósito: quem recarrega o board é o
   * nosso `limitPerStage`, e deixá-lo anexar por conta própria duplicaria os
   * mesmos registros.
   *
   * O limite é do board inteiro, não da coluna, então rolar uma coluna até o
   * fim recarrega todas — mesmo comportamento do botão "Carregar mais cards",
   * só que com o gatilho também dentro da coluna. Paginação por stage exigiria
   * o endpoint aceitar limite por coluna (ver seção 6.2.2 do MEMORIA_TECNICA).
   */
  const handleLaneScroll = useCallback(
    async (_nextPage, laneId) => {
      if (!loadingMoreRef.current && board?.[laneId]?.hasMore) {
        loadingMoreRef.current = true;
        setLimitPerStage((previous) => previous + CARDS_PER_STAGE_STEP);
      }
      return [];
    },
    [board]
  );

  const laneBackground =
    theme.mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.06)";

  const boardData = useMemo(() => {
    if (!board) return { lanes: [] };

    // Object.keys preserva a ordem em que o backend montou as colunas
    // (OPPORTUNITY_BOARD_STAGES) — a ordem das colunas é decisão do servidor.
    const lanes = Object.keys(board).map((stage) => {
      const column = board[stage];
      const visible = (column.opportunities || []).filter(matchesFilters);

      return {
        id: stage,
        title: column.stageLabel,
        label:
          visible.length === column.count
            ? `${column.count}`
            : `${visible.length} de ${column.count}`,
        style: {
          backgroundColor: laneBackground,
          width: 300,
          maxHeight: "100%",
        },
        cards: visible.map((opportunity) => ({
          id: String(opportunity.id),
          title: opportunity.contact?.name || opportunity.policy?.clientNameRaw || "Sem nome",
          draggable: rules.canMoveCards,
          style: {
            borderLeft: `4px solid ${
              isTerminalStage(stage)
                ? stageBadgeColor(stage)
                : urgencyColor(opportunity.urgencyBucket)
            }`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
          description: (
            <OpportunityCard
              opportunity={opportunity}
              stage={stage}
              onOpenTicket={handleOpenTicket}
              onCreateContact={handleOpenCreateContact}
              onSaveNotes={handleSaveNotes}
              onSaveKanbanFields={handleSaveKanbanFields}
              canEditKanbanFields={rules.canEditKanbanFields}
              canSetCelular={rules.canSetCelular}
            />
          ),
        })),
      };
    });

    return { lanes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, matchesFilters, laneBackground, theme, rules.canMoveCards, rules.canEditKanbanFields, rules.canSetCelular]);

  const renderBoard = () => {
    if (loading) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    const totalCards = board
      ? Object.values(board).reduce((total, column) => total + column.count, 0)
      : 0;

    if (totalCards === 0) {
      return (
        <Paper className={classes.hint} variant="outlined">
          <Typography variant="subtitle1" gutterBottom>
            Nenhuma oportunidade no Kanban ainda.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Os cards nascem de toda apólice ativa, com ou sem contato de WhatsApp vinculado — o
            selo "não conciliado" e o botão "Cadastrar contato" aparecem direto no card para quem
            ainda não tem um.
          </Typography>
        </Paper>
      );
    }

    return (
      <div className={classes.boardWrapper}>
        <Board
          data={boardData}
          handleDragEnd={handleDragEnd}
          onLaneScroll={handleLaneScroll}
          cardDraggable={rules.canMoveCards}
          laneDraggable={false}
          hideCardDeleteIcon
          style={{ backgroundColor: "transparent", height: "100%" }}
        />
      </div>
    );
  };

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={ticketModalOpen}
        initialContact={ticketContact}
        onClose={handleCloseTicketModal}
      />

      <CreateContactModal
        open={contactModalOpen}
        opportunity={contactModalOpportunity}
        onClose={handleCloseCreateContact}
        onSuccess={handleContactCreated}
      />

      {rules.canSetCelular && (
      <SetCelularModal
        open={celularModalOpen}
        onClose={handleCloseCelularModal}
        // policyId, não cpfCnpj: opportunityQuery.ts não traz cpfCnpj para o
        // board (dado sensível) — o controller resolve o documento no
        // servidor a partir da apólice, escopado por companyId.
        policyId={celularModalOpportunity?.policy?.id}
        initialName={celularModalOpportunity?.policy?.clientNameRaw}
        onSuccess={() => {
          // PUT /contatos/celular não devolve o card/Opportunity recalculado
          // (diferente de create-contact, que já traz o contato pronto) —
          // mais simples e seguro recarregar o board, mesmo padrão já usado
          // em handleRecalculate/handleDragEnd.
          fetchBoard();
        }}
      />
      )}

      <MainHeader>
        <Title>Oportunidades de Renovação</Title>
        <MainHeaderButtonsWrapper>
          {rules.canRecalculate ? (
            <Tooltip title="Recalcula urgência e dias até o vencimento de todas as apólices agora, sem esperar o processamento diário das 06:00">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CachedIcon />}
                  disabled={recalculating}
                  onClick={handleRecalculate}
                >
                  {recalculating ? "Recalculando..." : "Recalcular"}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {rules.showExtraTabs ? (
      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Kanban" value="board" />
        <Tab label="Fila de reativação" value="reactivation" />
        <Tab label="Cancelados" value="cancelled" />
      </Tabs>
      ) : null}

      <div className={classes.content}>
        {tab === "board" ? (
          <>
            <Paper className={classes.toolbar} variant="outlined">
              <TextField
                className={classes.search}
                size="small"
                variant="outlined"
                placeholder="Buscar cliente, produto ou seguradora"
                value={searchParam}
                onChange={(event) => setSearchParam(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />

              <div className={classes.summary}>
                {URGENCY_ORDER.map((bucket) => {
                  const active = urgencyFilter.includes(bucket);
                  return (
                    <Chip
                      key={bucket}
                      size="small"
                      className={active ? classes.summaryChip : undefined}
                      label={`${URGENCY_CONFIG[bucket].label}: ${urgencySummary[bucket] || 0}`}
                      variant={active ? "default" : "outlined"}
                      onClick={() => toggleUrgency(bucket)}
                      style={
                        active
                          ? { backgroundColor: URGENCY_CONFIG[bucket].color }
                          : { borderColor: URGENCY_CONFIG[bucket].color, color: URGENCY_CONFIG[bucket].color }
                      }
                    />
                  );
                })}
                {anyLaneHasMore ? (
                  <Button
                    size="small"
                    onClick={() => setLimitPerStage((previous) => previous + CARDS_PER_STAGE_STEP)}
                  >
                    Carregar mais cards
                  </Button>
                ) : null}
                {lanesAtLimit.length > 0 ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Lista truncada: ${lanesAtLimit.join(", ")}`}
                    style={{ borderColor: "#EF6C00", color: "#EF6C00" }}
                  />
                ) : null}
              </div>
            </Paper>

            {renderBoard()}
          </>
        ) : null}

        {rules.showExtraTabs && tab === "reactivation" ? (
          <OpportunityTable
            endpoint="/opportunities/reactivation-queue"
            params={{ limit: 20 }}
            showNextContact
            onOpenTicket={handleOpenTicket}
            emptyTitle="Nenhum cliente para reabordar hoje."
            emptyHint={
              "Esta fila traz quem foi marcado como perdido para a concorrência quando chega a " +
              "data de voltar a procurar — um ano depois do vencimento, que é quando o seguro do " +
              "concorrente vence. Para agir sobre quem já saiu da carteira agora, use Recuperação de Carteira."
            }
          />
        ) : null}

        {rules.showExtraTabs && tab === "cancelled" ? (
          <OpportunityTable
            endpoint="/opportunities"
            params={{ stage: "cancelado", limit: 20 }}
            onOpenTicket={handleOpenTicket}
            emptyTitle="Nenhuma oportunidade cancelada."
            emptyHint="Cards cancelados pelo cliente, ou fechados automaticamente por troca de bem, aparecem aqui."
          />
        ) : null}
      </div>
    </MainContainer>
  );
};

export default Opportunities;
